import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, QrCode, Users, Bell, Check, ChevronRight, X, Rocket, PartyPopper } from 'lucide-react';
import { getProperties, getTeamMembers, getNotificationSettings, markOnboardingSeen } from '../lib/api';

const DISMISS_KEY = 'gc_onboarding_dismissed';
const QR_KEY = 'gc_onboarding_qr_done';

// Interactive, self-updating setup checklist for new admin hosts.
export default function OnboardingChecklist() {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(localStorage.getItem(DISMISS_KEY) === '1');
  const [loaded, setLoaded] = useState(false);
  const [properties, setProperties] = useState([]);
  const [teamCount, setTeamCount] = useState(0);
  const [notifOn, setNotifOn] = useState(false);
  const [qrDone, setQrDone] = useState(localStorage.getItem(QR_KEY) === '1');

  const refresh = useCallback(async () => {
    try {
      const [propsRes, teamRes, notifRes] = await Promise.allSettled([
        getProperties(),
        getTeamMembers(),
        getNotificationSettings(),
      ]);
      if (propsRes.status === 'fulfilled') setProperties(propsRes.value.data.properties || []);
      if (teamRes.status === 'fulfilled') setTeamCount((teamRes.value.data.members || []).length);
      if (notifRes.status === 'fulfilled') {
        const s = notifRes.value.data || {};
        setNotifOn(!!s.pushNotifications || !!s.emailCheckoutNotifications);
      }
    } catch (e) { /* silent */ } finally { setLoaded(true); }
  }, []);

  useEffect(() => {
    if (dismissed) return;
    refresh();
    const onFocus = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [dismissed, refresh]);

  const hasProperty = properties.length > 0;
  const firstPropertyId = properties[0]?.id;

  const markQrDone = () => { localStorage.setItem(QR_KEY, '1'); setQrDone(true); };

  const steps = [
    {
      key: 'property',
      title: 'Create your first property',
      desc: 'Add a flat, room or rental to get started',
      icon: Home,
      done: hasProperty,
      onClick: () => navigate('/properties'),
    },
    {
      key: 'qr',
      title: 'Share your checkout QR',
      desc: 'Download & print it, or paste the link into your guest messages — or both',
      icon: QrCode,
      done: qrDone && hasProperty,
      onClick: () => {
        if (hasProperty) { markQrDone(); navigate(`/properties/${firstPropertyId}`); }
        else navigate('/properties');
      },
    },
    {
      key: 'team',
      title: 'Invite your team',
      desc: 'Add cleaners or staff so they get checkout alerts',
      icon: Users,
      optional: true,
      done: teamCount > 1,
      onClick: () => navigate('/team'),
    },
    {
      key: 'notifications',
      title: 'Turn on notifications',
      desc: 'Get alerted the moment a guest checks out',
      icon: Bell,
      optional: true,
      done: notifOn,
      onClick: () => navigate('/settings'),
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  const total = steps.length;
  const pct = Math.round((doneCount / total) * 100);
  const allDone = doneCount === total;

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, '1'); setDismissed(true); markOnboardingSeen().catch(() => {}); };

  // Hide until data is loaded (avoids a flash of empty checklist), and once dismissed.
  if (dismissed || !loaded) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, height: 0 }}
        className="mb-5 rounded-3xl overflow-hidden shadow-lg shadow-blue-500/10 border border-blue-100 bg-white"
        data-testid="onboarding-checklist"
      >
        {/* Header */}
        <div className="relative px-5 pt-5 pb-4 text-white overflow-hidden" style={{ background: 'linear-gradient(135deg,#2563EB 0%,#059669 100%)' }}>
          <div className="absolute -top-8 -right-6 w-28 h-28 bg-white/15 rounded-full blur-2xl pointer-events-none" />
          <div className="relative z-10 flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                {allDone ? <PartyPopper size={22} /> : <Rocket size={22} />}
              </div>
              <div className="min-w-0">
                <h3 className="font-extrabold text-[17px] leading-tight" data-testid="onboarding-title">
                  {allDone ? "You're all set! 🎉" : 'Get set up in a few steps'}
                </h3>
                <p className="text-white/85 text-xs mt-0.5">
                  {allDone ? 'Your account is ready to go.' : `${doneCount} of ${total} done — you're almost there`}
                </p>
              </div>
            </div>
            <button
              onClick={dismiss}
              className="flex-shrink-0 w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
              aria-label="Dismiss"
              data-testid="onboarding-dismiss"
            >
              <X size={15} />
            </button>
          </div>
          {/* Progress bar */}
          <div className="relative z-10 mt-3 h-2 rounded-full bg-white/25 overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-white"
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              data-testid="onboarding-progress"
            />
          </div>
        </div>

        {/* Steps */}
        <div className="divide-y divide-slate-100">
          {steps.map((step) => (
            <button
              key={step.key}
              onClick={step.onClick}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors group"
              data-testid={`onboarding-step-${step.key}`}
            >
              <div
                className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                  step.done ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-blue-50 group-hover:text-blue-500'
                }`}
              >
                {step.done ? <Check size={18} strokeWidth={3} /> : <step.icon size={18} />}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold leading-tight ${step.done ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                  {step.title}
                  {step.optional && !step.done && (
                    <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-400">Optional</span>
                  )}
                </p>
                {!step.done && <p className="text-xs text-slate-500 mt-0.5 truncate">{step.desc}</p>}
              </div>
              {!step.done && <ChevronRight size={18} className="text-slate-300 flex-shrink-0 group-hover:text-blue-400 transition-colors" />}
            </button>
          ))}
        </div>

        {allDone && (
          <div className="p-4">
            <button
              onClick={dismiss}
              className="w-full h-11 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-colors"
              data-testid="onboarding-finish"
            >
              Done
            </button>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
